import {MonorailTracker} from '__deprecated__/components/discountApp/analytics';
import {constraintWidthInViewport} from '__deprecated__/common/utils/constraintWidthInViewport';

import {ATTRIBUTE_ANALYTICS_TRACE_ID} from '../../../constants/loginButton';
import {
  LoginWithShopSdkPageName,
  LoginWithShopSdkUserAction,
} from '../../../common/analytics';
import {ShopSheetModal} from '../../../common/shop-sheet-modal/shop-sheet-modal';
import {copyTemplateToDom, updateAttribute} from '../../../common/utils';
import {
  createStatusIndicator,
  ShopStatusIndicator,
  StatusIndicatorLoader,
} from '../../../common/shop-status-indicator';
import {
  createShopDiscountCode,
  ShopDiscountCode,
} from '../components/shop-discount-code';
import {
  SDA_PHONE_CAPTURE_CLASS,
  SHOP_DISCOUNT_AUTH_HTML,
  DISCLOSURE_TEXT_STYLE,
} from '../constants';
import {DiscountAppProcessingStatus as ProcessingStatus} from '../../../types';

interface DiscountCodeView {
  code?: string;
  saved?: boolean;
}

interface StatusIndicatorView {
  branded: boolean;
  status: ProcessingStatus;
  message: string;
}

interface ButtonState {
  label: string;
  onClick: () => void;
}

export interface ViewState {
  headerDivider?: boolean;
  title?: string;
  description?: string;
  headerVisible?: boolean;
  processingElementVisible?: boolean;
  processingUserElementVisible?: boolean;
  processingUser?: string;
  phoneCaptureContainerVisible?: boolean;
  phoneCaptureButtonsContainerVisible?: boolean;
  phoneConsentConfirmButton?: ButtonState;
  phoneConsentDeclineButton?: ButtonState;
  disclosureHeading?: string;
  disclosureText?: string;
  iframeVisible?: boolean;
  discountVisible?: boolean;
  discountCode?: DiscountCodeView | null;
  statusIndicator?: StatusIndicatorView;
}

export default class DiscountAuthView {
  private _rootElement: ShadowRoot;
  private _sheetModal!: ShopSheetModal;
  private _headerElement: HTMLDivElement;
  private _headerTitle: HTMLElement;
  private _headerDescription: HTMLElement;
  private _iframe: HTMLIFrameElement;
  private _processingElement: HTMLDivElement;
  private _processingUserElement: HTMLDivElement;
  private _processingStatusElement: HTMLDivElement;
  private _phoneCaptureContainer: HTMLElement;
  private _disclosureIframe: HTMLIFrameElement;
  private _disclosureContainer: HTMLDivElement | undefined;
  private _phoneCaptureButtonsContainer: HTMLElement;
  private _phoneConsentConfirmButton: HTMLButtonElement;
  private _phoneConsentDeclineButton: HTMLButtonElement;
  private _discountCodeElement?: ShopDiscountCode;
  private _statusIndicator?: ShopStatusIndicator;

  private _monorailTracker: MonorailTracker | undefined;

  constructor(
    rootElement: ShadowRoot,
    onModalCloseRequest: () => void,
    private _onOpen: () => void,
    private _onClose: () => void,
  ) {
    this._rootElement = rootElement;

    copyTemplateToDom(
      SHOP_DISCOUNT_AUTH_HTML,
      'shop-discount-auth-landing',
      this._rootElement,
    );

    this._sheetModal = this._rootElement.querySelector('shop-sheet-modal')!;
    this._headerElement = this._rootElement.querySelector('.sda-header')!;
    this._headerTitle = this._rootElement.querySelector('.sda-header-title')!;
    this._headerDescription = this._rootElement.querySelector(
      '.sda-header-description',
    )!;
    this._iframe = this._rootElement.querySelector('.sda-iframe')!;

    this._discountCodeElement = createShopDiscountCode();
    this._sheetModal.prepend(this._discountCodeElement);

    this._processingElement =
      this._rootElement.querySelector('.sda-processing')!;
    this._processingUserElement = this._processingElement.querySelector(
      '.sda-processing-user',
    )!;
    this._processingStatusElement = this._processingElement.querySelector(
      '.sda-processing-status',
    )!;

    this._phoneCaptureContainer = this._rootElement.querySelector(
      `.${SDA_PHONE_CAPTURE_CLASS}`,
    )!;
    this._phoneCaptureButtonsContainer =
      this._phoneCaptureContainer.querySelector(`.sda-phone-capture-buttons`)!;
    this._phoneConsentConfirmButton = this._phoneCaptureContainer.querySelector(
      `.sda-phone-capture-confirm`,
    )!;
    this._phoneConsentDeclineButton = this._phoneCaptureContainer.querySelector(
      `.sda-phone-capture-decline`,
    )!;

    this._disclosureIframe = this._rootElement.querySelector(
      'iframe.sda-disclosure-text-container',
    )! as HTMLIFrameElement;

    this._sheetModal.addEventListener('modalcloserequest', onModalCloseRequest);

    // For passkeys
    updateAttribute(this._iframe, 'allow', 'publickey-credentials-get *');
  }

  /*
   * The primary function to perform DOM updates. Allows complex view state changes to be exrpressed as one function call
   * with one ViewState object representing the new state.
   *
   * Each attribute controls a section of the DOM.
   * - If an attribute is passed in, its corresponding DOM element is updated (i.e. shown/hidden, content is updated, etc).
   * - If an attribute is `null` its corresponding DOM element is removed.
   * - If an attribute is `undefined` its corresponding DOM element is left untouched.
   */
  updateSheetContent({
    headerVisible,
    title,
    description,
    headerDivider,
    processingElementVisible,
    processingUserElementVisible,
    processingUser,
    phoneCaptureContainerVisible,
    phoneCaptureButtonsContainerVisible,
    phoneConsentConfirmButton,
    phoneConsentDeclineButton,
    disclosureHeading,
    disclosureText,
    iframeVisible,
    statusIndicator,
    discountVisible,
    discountCode,
  }: ViewState) {
    if (headerVisible !== undefined) {
      setElementVisible(this._headerElement, headerVisible);
    }
    if (title !== undefined) {
      this._headerTitle.textContent = title;
    }
    if (description !== undefined) {
      this._headerDescription.textContent = description;
    }
    if (headerDivider !== undefined) {
      this._headerElement.classList.toggle('sda-header-divider', headerDivider);
    }

    if (processingUserElementVisible !== undefined) {
      setElementVisible(
        this._processingUserElement,
        processingUserElementVisible,
      );
    }
    if (processingElementVisible !== undefined) {
      setElementVisible(this._processingElement, processingElementVisible);
    }
    if (processingUser !== undefined) {
      this._processingUserElement.textContent = processingUser;
    }

    if (phoneCaptureContainerVisible !== undefined) {
      setElementVisible(
        this._phoneCaptureContainer,
        phoneCaptureContainerVisible,
      );
    }
    if (phoneCaptureButtonsContainerVisible !== undefined) {
      setElementVisible(
        this._phoneCaptureButtonsContainer,
        phoneCaptureButtonsContainerVisible,
      );
    }
    if (phoneConsentConfirmButton !== undefined) {
      this._phoneConsentConfirmButton.innerText =
        phoneConsentConfirmButton.label;
      this._phoneConsentConfirmButton.onclick =
        phoneConsentConfirmButton.onClick;
    }
    if (phoneConsentDeclineButton !== undefined) {
      this._phoneConsentDeclineButton.innerText =
        phoneConsentDeclineButton.label;
      this._phoneConsentDeclineButton.onclick =
        phoneConsentDeclineButton.onClick;
    }
    if (disclosureHeading !== undefined) {
      this._rootElement.querySelector('.sda-disclosure-heading')!.textContent =
        disclosureHeading;
    }
    if (disclosureText !== undefined) {
      this._updateDisclosureText(disclosureText);
    }

    if (iframeVisible === true) {
      this._showIframe();
    } else if (iframeVisible === false) {
      this._hideIframe();
    }

    if (statusIndicator) {
      if (!this._statusIndicator) {
        this._initStatusIndicator(statusIndicator.branded);
      }
      const {status, message} = statusIndicator;
      this._statusIndicator?.setStatus({status, message});
    }

    if (discountVisible !== undefined && this._discountCodeElement) {
      setElementVisible(this._discountCodeElement, discountVisible);
    }
    if (discountCode) {
      this._updateDiscountCode(discountCode);
    }
  }

  showPhoneConsentScreen({
    title,
    disclosureHeading,
    disclosureText,
    confirmButtonLabel,
    declineButtonLabel,
    discountCode,
    onConfirm,
    onDecline,
  }: {
    title: string;
    disclosureHeading: string;
    disclosureText: string;
    confirmButtonLabel: string;
    declineButtonLabel: string;
    discountCode?: DiscountCodeView | null;
    onConfirm: () => void;
    onDecline: () => void;
  }): void {
    this.updateSheetContent({
      title,
      description: '',
      iframeVisible: false,
      headerDivider: false,
      processingElementVisible: false,
      phoneCaptureContainerVisible: true,
      disclosureHeading,
      disclosureText,
      discountCode,
      phoneConsentConfirmButton: {
        label: confirmButtonLabel,
        onClick: () => {
          this._monorailTracker?.trackShopPayLoginWithShopSdkUserAction({
            userAction: LoginWithShopSdkUserAction.PhoneConsentProvided,
          });
          onConfirm();
        },
      },
      phoneConsentDeclineButton: {
        label: declineButtonLabel,
        onClick: () => {
          this._monorailTracker?.trackShopPayLoginWithShopSdkUserAction({
            userAction: LoginWithShopSdkUserAction.PhoneConsentDeclined,
          });
          onDecline();
        },
      },
    });
    this._monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.PhoneConsent,
    });
  }

  showTitleAndDescriptionOnlyScreen({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) {
    this.updateSheetContent({
      title,
      description,
      processingElementVisible: false,
      phoneCaptureContainerVisible: false,
      processingUserElementVisible: false,
    });
  }

  getIframe(): HTMLIFrameElement {
    return this._iframe;
  }

  resizeIframe(height: number, width: number, onSuccess: () => void) {
    if (!this._iframe) return;

    this._iframe.style.height = `${height}px`;
    this._iframe.style.width = `${constraintWidthInViewport(width, this._iframe)}px`;
    onSuccess();
  }

  toggleModalBusy(busy: boolean) {
    if (busy) {
      this._sheetModal.setAttribute('busy', 'true');
    } else {
      this._sheetModal.removeAttribute('busy');
    }
  }

  isModalBusy(): boolean {
    return this._sheetModal.getAttribute('busy') === 'true';
  }

  setModalAnalyticsTraceId(analyticsTraceId: string): void {
    this._sheetModal.setAttribute(
      ATTRIBUTE_ANALYTICS_TRACE_ID,
      analyticsTraceId,
    );
  }

  async showModal() {
    const result = await this._sheetModal.open();
    if (result) {
      this._onOpen();
    }
  }

  async hideModal(): Promise<void | Animation> {
    return this._sheetModal
      .close()
      .then((animation) => {
        if (animation) {
          this._hideIframe();
          this._onClose();
        }
      })
      .catch(() => {
        this._hideIframe();
        this._onClose();
      });
  }

  reset() {
    this.hideModal();
    this.updateSheetContent({
      headerVisible: false,
      processingElementVisible: false,
      discountCode: {
        code: '',
        saved: false,
      },
    });
  }

  setMonorailTracker(monorailTracker: MonorailTracker) {
    this._monorailTracker = monorailTracker;
    this._sheetModal.setMonorailTracker(this._monorailTracker!);
  }

  private _initStatusIndicator(branded: boolean) {
    const loaderType: StatusIndicatorLoader = branded
      ? StatusIndicatorLoader.Branded
      : StatusIndicatorLoader.Regular;
    this._statusIndicator = createStatusIndicator(loaderType);
    this._processingStatusElement.appendChild(this._statusIndicator);
  }

  private _showIframe(): void {
    setElementVisible(this._iframe, true);
  }

  private _hideIframe(): void {
    setElementVisible(this._iframe, false);
  }

  private _updateDiscountCode({code, saved}: DiscountCodeView): void {
    if (code !== undefined) {
      if (code) {
        this._discountCodeElement?.setAttribute('code', code);
        this._monorailTracker?.trackPageImpression({
          page: LoginWithShopSdkPageName.DiscountShown,
        });
      } else {
        this._discountCodeElement?.removeAttribute('code');
      }
    }
    if (saved !== undefined) {
      if (saved) {
        this._discountCodeElement?.setAttribute('saved', '');
      } else {
        this._discountCodeElement?.removeAttribute('saved');
      }
    }
  }

  private _updateDisclosureText(disclosureText: string) {
    if (!this._disclosureContainer) {
      const disclosureIframeBody =
        this._disclosureIframe.contentDocument?.querySelector('body')!;
      disclosureIframeBody.innerHTML = DISCLOSURE_TEXT_STYLE;

      const disclosureContainer = document.createElement('div');
      disclosureContainer.setAttribute('class', 'sda-disclosure-text');
      disclosureIframeBody.appendChild(disclosureContainer);
      this._disclosureContainer = disclosureContainer;
    }

    this._disclosureContainer.innerHTML = disclosureText;

    // Resize iframe to match height of the disclosure text
    this._disclosureIframe.setAttribute(
      'height',
      `${this._disclosureContainer.getBoundingClientRect().height}px`,
    );
  }
}

/**
 * Set the visibility of a given element by toggling the `sda-hidden` class.
 * @param {HTMLElement} element The element to set the visibility of.
 * @param {boolean} visible Whether the element should be visible or not.
 */
function setElementVisible(element: HTMLElement, visible: boolean) {
  element.classList.toggle('sda-hidden', !visible);
}
